import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Stack,
  Button,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Icon,
  Flex,
  Divider,
  Badge,
  Image,
} from '@chakra-ui/react';
import { FaCheck, FaChartBar, FaArrowLeft, FaChartLine, FaDownload, FaClock, FaTachometerAlt, FaHistory, FaExclamationTriangle, FaChartPie } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { seoData } from '../utils/seoData';

const FeatureBox = ({ title, description, icon }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={6}
      rounded="xl"
      shadow="md"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-3px)', shadow: 'lg' }}
    >
      <Flex
        w={12}
        h={12}
        align="center"
        justify="center"
        color="white"
        rounded="full"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
        mb={3}
      >
        <Icon as={icon} w={6} h={6} />
      </Flex>
      <Heading size="md" mb={2}>
        {title}
      </Heading>
      <Text color="gray.600" fontSize="sm">
        {description}
      </Text>
    </Stack>
  );
};

const AnalyticsDashboardProduct = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const seo = seoData.analyticsDashboard[language] || seoData.analyticsDashboard.en;
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const translations = {
    en: {
      backButton: 'Back to Products',
      badgeLabel: 'New',
      heroTitle: 'Analytics Dashboard',
      heroDescription:
        'Track student performance and gain insights with comprehensive analytics and reporting tools. Make data-driven decisions to improve learning outcomes.',
      heroButton: 'Get Started',
      featuresHeading: 'Advanced Analytics Features',
      featuresDescription: 'Transform raw data into actionable insights with our powerful analytics tools',
      features: [
        {
          title: 'Real-Time Tracking',
          description: 'Monitor student performance in real-time with live dashboards and instant notifications.',
          icon: FaClock,
        },
        {
          title: 'Custom Reports',
          description: 'Generate detailed reports tailored to your specific needs with our flexible reporting engine.',
          icon: FaChartBar,
        },
        {
          title: 'Progress Visualization',
          description: 'Beautiful charts and graphs that make complex data easy to understand at a glance.',
          icon: FaChartLine,
        },
        {
          title: 'Predictive Analytics',
          description: 'AI-powered insights that predict future performance and identify at-risk students early.',
          icon: FaDownload,
        },
      ],
      benefitsHeading: 'Why Choose Analytics Dashboard?',
      benefitsDescription:
        'Stop guessing and start knowing. Our Analytics Dashboard gives you the insights you need to identify trends, spot problems early, and celebrate successes.',
      benefitsHighlightTitle: 'Data-Driven Insights',
      benefitsList: [
        'Comprehensive student performance metrics',
        'Class and individual comparisons',
        'Automated report scheduling',
        'Export to Excel, PDF, and CSV',
        'Interactive data visualizations',
        'Customizable dashboard layouts',
        'Historical data analysis',
        'Mobile-responsive analytics',
      ],
      comprehensiveSection: {
        title: 'Comprehensive Student Analytics',
        description:
          "Get a complete picture of each student's learning journey with detailed reports, historical data, and actionable insights.",
        cards: [
          {
            icon: FaTachometerAlt,
            title: 'Holistic Progress Overview',
            description:
              "Get a complete snapshot of each student's performance at a glance. Track overall progress, completion rates, and performance trends over time.",
            bullets: [
              'Overall performance scores and rankings',
              'Completion rates across all subjects',
              'Time spent and engagement metrics',
            ],
            imageSrc: '/screenshots/student-report.png',
            imageAlt: 'Holistic Student Progress View',
            imageOnLeft: true,
          },
          {
            icon: FaHistory,
            title: 'Detailed Problem Set History',
            description:
              'Track every problem set attempted with comprehensive historical data. See exactly what students worked on, when, and how they performed.',
            bullets: [
              'Chronological problem set timeline',
              'Performance scores for each attempt',
              'Subject and topic breakdowns',
            ],
            imageSrc: '/screenshots/student-report-2.png',
            imageAlt: 'Problem Set History',
            imageOnLeft: false,
          },
          {
            icon: FaExclamationTriangle,
            title: 'Incorrect Problems Collection',
            description:
              'Automatically collect and organize all problems students answered incorrectly. Perfect for targeted review and practice sessions.',
            bullets: [
              'Centralized repository of missed problems',
              'Filter by subject, difficulty, and date',
              'One-click retry and reassignment',
            ],
            imageSrc: '/screenshots/student-report-3.png',
            imageAlt: 'Collection of Incorrect Problems',
            imageOnLeft: true,
          },
          {
            icon: FaChartPie,
            title: 'Problem Type Struggle Analysis',
            description:
              'Identify exactly which problem types students struggle with most. Use data-driven insights to create targeted intervention strategies.',
            bullets: [
              'Visual breakdown of problem type performance',
              'Weakness identification algorithms',
              'Recommended practice recommendations',
            ],
            imageSrc: '/screenshots/student-report-4.png',
            imageAlt: 'Problem Type Insights',
            imageOnLeft: false,
          },
        ],
        highlightTitle: 'Data That Drives Results',
        highlightDescription:
          "Transform raw data into actionable insights. Our Analytics Dashboard doesn't just show you numbers—it tells you the story of each student's learning journey and guides you toward better outcomes.",
      },
      cta: {
        title: 'Ready to Unlock Your Data?',
        description:
          'Transform your educational data into actionable insights with our Analytics Dashboard.',
        primary: 'Contact Sales',
        secondary: 'View All Products',
      },
    },
    ko: {
      backButton: '제품 목록으로',
      badgeLabel: '신규',
      heroTitle: '분석 대시보드',
      heroDescription:
        '종합적인 분석과 보고 기능으로 학습 성과를 추적하고 인사이트를 확보하세요. 데이터 기반 의사결정으로 학습 효과를 극대화할 수 있습니다.',
      heroButton: '도입 상담하기',
      featuresHeading: '고급 분석 기능',
      featuresDescription: '강력한 분석 도구로 데이터를 실행 가능한 인사이트로 전환하세요.',
      features: [
        {
          title: '실시간 추적',
          description: '라이브 대시보드와 즉시 알림으로 학생 성과를 실시간으로 확인하세요.',
          icon: FaClock,
        },
        {
          title: '맞춤형 보고서',
          description: '유연한 보고 엔진으로 필요한 기준에 맞춘 상세 보고서를 생성하세요.',
          icon: FaChartBar,
        },
        {
          title: '진행 상황 시각화',
          description: '복잡한 데이터를 직관적인 차트와 그래프로 한눈에 파악할 수 있습니다.',
          icon: FaChartLine,
        },
        {
          title: '예측 분석',
          description: 'AI가 미래 성과를 예측하고 위험 학생을 조기에 발견합니다.',
          icon: FaDownload,
        },
      ],
      benefitsHeading: '분석 대시보드를 선택해야 하는 이유',
      benefitsDescription:
        '감(感)에 의존하지 마세요. 분석 대시보드는 추세를 파악하고 문제를 조기에 발견하며 성과를 축하할 수 있도록 도와줍니다.',
      benefitsHighlightTitle: '데이터 기반 인사이트',
      benefitsList: [
        '학생 성과를 다각도로 분석',
        '반 전체와 개인 성과 비교',
        '자동화된 보고 일정 관리',
        'Excel·PDF·CSV로 내보내기',
        '대화형 데이터 시각화',
        '맞춤형 대시보드 레이아웃',
        '과거 데이터 분석',
        '모바일 친화적 분석 환경',
      ],
      comprehensiveSection: {
        title: '학생 분석을 한눈에',
        description:
          '상세 보고서와 과거 기록, 실행 가능한 인사이트로 학습 여정을 완벽하게 파악하세요.',
        cards: [
          {
            icon: FaTachometerAlt,
            title: '종합 성취 개요',
            description:
              '학생별 학업 성취도를 한눈에 확인하고 전체 진행률과 성과 추이를 추적하세요.',
            bullets: [
              '전체 성취도와 순위 확인',
              '과목별 완료율 추적',
              '학습 시간과 참여도 분석',
            ],
            imageSrc: '/screenshots/student-report.png',
            imageAlt: '학생 성취 개요 화면',
            imageOnLeft: true,
          },
          {
            icon: FaHistory,
            title: '문제 세트 이력 분석',
            description:
              '학생이 언제 어떤 문제집을 풀었는지 세부 기록과 성과를 확인할 수 있습니다.',
            bullets: [
              '연대기별 문제 세트 기록',
              '시도별 점수 변화 확인',
              '과목·주제별 성과 분류',
            ],
            imageSrc: '/screenshots/student-report-2.png',
            imageAlt: '문제 세트 이력 화면',
            imageOnLeft: false,
          },
          {
            icon: FaExclamationTriangle,
            title: '오답 문제 모음',
            description:
              '학생이 틀린 문제를 자동으로 모아 맞춤형 복습 자료를 제공합니다.',
            bullets: [
              '오답 문제 중앙 관리',
              '과목·난이도·날짜별 필터',
              '한 번의 클릭으로 재배포',
            ],
            imageSrc: '/screenshots/student-report-3.png',
            imageAlt: '오답 문제 모음 화면',
            imageOnLeft: true,
          },
          {
            icon: FaChartPie,
            title: '취약 유형 분석',
            description:
              '학생이 어려워하는 문제 유형을 정확히 파악하고 목표형 보강 수업을 설계하세요.',
            bullets: [
              '문제 유형별 성과 시각화',
              '취약 영역 자동 진단',
              '맞춤형 연습 문제 추천',
            ],
            imageSrc: '/screenshots/student-report-4.png',
            imageAlt: '취약 유형 분석 화면',
            imageOnLeft: false,
          },
        ],
        highlightTitle: '데이터로 완성하는 학습 전략',
        highlightDescription:
          '분석 대시보드는 단순한 수치를 넘어 학습 여정을 이해하고 더 나은 결과로 안내합니다.',
      },
      cta: {
        title: '지금 데이터 기반 학습을 시작하세요',
        description:
          '분석 대시보드로 교육 데이터를 인사이트로 전환하고 의사결정을 혁신하세요.',
        primary: '영업팀에 문의하기',
        secondary: '전체 제품 보기',
      },
    },
  };

  const t = translations[language] || translations.en;

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/products/analytics-dashboard"
        lang={language}
        ogImage="/screenshots/student-report.png"
      />
      <Box>
        {/* Back Button */}
        <Box bg={useColorModeValue('white', 'gray.800')} py={4} borderBottom="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>
        <Container maxW="container.xl">
          <Button
            leftIcon={<FaArrowLeft />}
            variant="ghost"
            onClick={() => navigate('/products')}
            _hover={{ color: '#FF6A5F' }}
          >
            {t.backButton}
          </Button>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-br, #FF6A5F, #A63FF5, #C56CFF)"
        color="white"
        py={20}
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Flex align="center" gap={4}>
              <Flex
                w={20}
                h={20}
                align="center"
                justify="center"
                color="white"
                rounded="full"
                bg="whiteAlpha.200"
              >
                <Icon as={FaChartBar} w={10} h={10} />
              </Flex>
              <Badge
                bgGradient="linear(to-r, #FF8FA5, #C56CFF)"
                color="white"
                fontSize="md"
                px={4}
                py={2}
                rounded="full"
              >
                {t.badgeLabel}
              </Badge>
            </Flex>
            <Heading size="3xl" fontWeight="extrabold">
              {t.heroTitle}
            </Heading>
            <Text fontSize="xl" maxW="800px">
              {t.heroDescription}
            </Text>
            <Button
              size="lg"
              bg="white"
              color="#A63FF5"
              px={8}
              _hover={{
                bg: 'gray.100',
                transform: 'translateY(-2px)',
                shadow: 'xl',
              }}
              transition="all 0.3s"
              onClick={() => navigate('/contact')}
            >
              {t.heroButton}
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{t.featuresHeading}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                {t.featuresDescription}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {t.features.map((feature) => (
                <FeatureBox key={feature.title} {...feature} />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* Benefits Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="2xl">{t.benefitsHeading}</Heading>
              <Text fontSize="lg" color="gray.600">
                {t.benefitsDescription}
              </Text>
              <List spacing={3}>
                {t.benefitsList.map((benefit, index) => (
                  <ListItem key={index} display="flex" alignItems="center">
                    <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                    <Text>{benefit}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>

            <Box
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              h="400px"
              rounded="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              shadow="2xl"
            >
              <VStack spacing={4} color="white">
                <Icon as={FaChartLine} w={20} h={20} />
                <Heading size="xl">{t.benefitsHighlightTitle}</Heading>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Divider />

      {/* Comprehensive Student Insights Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{t.comprehensiveSection.title}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="800px">
                {t.comprehensiveSection.description}
              </Text>
            </VStack>

            {t.comprehensiveSection.cards.map((card, index) => (
              <SimpleGrid
                key={card.title}
                columns={{ base: 1, lg: 2 }}
                spacing={10}
                w="full"
                alignItems="center"
              >
                <Box order={{ base: 2, lg: card.imageOnLeft ? 1 : 2 }}>
                  <Image
                    src={card.imageSrc}
                    alt={card.imageAlt}
                    rounded="2xl"
                    shadow="2xl"
                    w="full"
                    h="auto"
                    objectFit="cover"
                    border="1px"
                    borderColor={borderColor}
                  />
                </Box>

                <VStack
                  align="flex-start"
                  spacing={4}
                  order={{ base: 1, lg: card.imageOnLeft ? 2 : 1 }}
                >
                  <Flex
                    w={14}
                    h={14}
                    align="center"
                    justify="center"
                    color="white"
                    rounded="full"
                    bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                  >
                    <Icon as={card.icon} w={6} h={6} />
                  </Flex>
                  <Heading size="xl">{card.title}</Heading>
                  <Text fontSize="lg" color="gray.600">
                    {card.description}
                  </Text>
                  <List spacing={3}>
                    {card.bullets.map((bullet, idx) => (
                      <ListItem key={idx} display="flex" alignItems="center">
                        <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                        <Text>{bullet}</Text>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </SimpleGrid>
            ))}

            {/* Key Analytics Feature Highlight */}
            <Box
              w="full"
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              p={10}
              rounded="2xl"
              shadow="2xl"
            >
              <VStack spacing={6} color="white">
                <Icon as={FaChartLine} w={16} h={16} />
                <Heading size="xl" textAlign="center">
                  {t.comprehensiveSection.highlightTitle}
                </Heading>
                <Text fontSize="lg" textAlign="center" maxW="700px">
                  {t.comprehensiveSection.highlightDescription}
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* CTA Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <VStack
            spacing={6}
            bg={useColorModeValue('white', 'gray.800')}
            p={12}
            rounded="2xl"
            shadow="xl"
            textAlign="center"
          >
            <Heading size="2xl">{t.cta.title}</Heading>
            <Text fontSize="lg" color="gray.600" maxW="600px">
              {t.cta.description}
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
              <Button
                size="lg"
                bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                color="white"
                px={8}
                _hover={{
                  bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                  transform: 'translateY(-2px)',
                  shadow: 'xl',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/contact')}
              >
                {t.cta.primary}
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="#A63FF5"
                color="#A63FF5"
                px={8}
                _hover={{
                  bg: '#A63FF5',
                  color: 'white',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/products')}
              >
                {t.cta.secondary}
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default AnalyticsDashboardProduct;

